import { NgIf } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  forwardRef,
  Input,
  OnDestroy,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputErrorComponent } from '@werfy/ui';
import { Subscription } from 'rxjs';
import { InputComponent, InputInterface } from '../input';
import { RadioButtonComponent } from '../radio-button';
import { RadioOptionComponent } from '../radio-option';

@Component({
  selector: 'wp-radio-input',
  templateUrl: 'radio-input.component.html',
  styleUrl: 'radio-input.component.scss',
  imports: [InputErrorComponent, NgIf],
  providers: [{ provide: InputComponent, useExisting: forwardRef(() => RadioInputComponent), multi: true }],
})
export class RadioInputComponent implements ControlValueAccessor, InputInterface, AfterContentInit, OnDestroy {
  @ContentChildren(RadioOptionComponent) public options!: QueryList<RadioOptionComponent>;
  @ContentChildren(RadioButtonComponent) public buttons!: QueryList<RadioButtonComponent>;

  @Input() public isVertical: boolean = false;
  @Input() public name?: string;

  public value?: string | null;
  public isDisabled: boolean = false;
  public subscriptions: Subscription = new Subscription();
  public onChange!: (value: string | number) => void;
  public onTouched!: () => void;

  constructor(@Optional() @Self() public control: NgControl) {
    if (control) {
      control.valueAccessor = this;
    }
  }

  public ngAfterContentInit(): void {
    this.options.forEach((option) => this.handleOption(option));
    this.buttons.forEach((option) => this.handleOption(option));
    setTimeout(() => {
      this.options.forEach((option) => this.checkIsActive(option));
      this.buttons.forEach((option) => this.checkIsActive(option));
    }, 0);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public registerOnChange(onChange: (value: string | number | null) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public writeValue(value: string | null): void {
    this.value = value;
  }

  public onValueChange(value: string): void {
    this.writeValue(value);
    this.onChange(value);
    this.onTouched();
  }

  private handleOption(option: RadioOptionComponent): void {
    const subscription: Subscription = option.selectOption.subscribe({
      next: () => this.handleSelectOption(option),
    });
    this.subscriptions.add(subscription);
  }

  private handleSelectOption(selectedOption: RadioOptionComponent | RadioButtonComponent): void {
    this.options.forEach((option: RadioOptionComponent): boolean => (option.isSelected = option === selectedOption));
    this.buttons.forEach((button: RadioButtonComponent): boolean => (button.isSelected = button === selectedOption));
    this.onValueChange(selectedOption.value);
  }

  private checkIsActive(option: RadioOptionComponent): void {
    option.isSelected = option.value === this.value;
  }
}
